import React, {
  FC,
} from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Checkbox } from '@mui/material';
import { DARK_GREY } from '../../../constants/theme';
import { DragIndicatorIcon } from '../../Icon';
import NameEditor from './NameEditor';
import {
  IClipFieldUpdates,
  selectIsInit,
  toggleClipSelection,
  updateClipFields,
} from '../../../state/clipList';
import { IAppState } from '../../../../../state/reducers';
import { IVideoClip } from '../../../../../api/vault/clipList';
import { ILookupOption, optionsToReactSelect, selectExchangeQualifiersOptions } from '../../../../../state/lookups';
import { SelectOptionType } from '../../../../../utils/selection';
import StyledSelect from '../../../../Shared/Select';

const Container = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;
const NameAndDurationContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 60%;
  align-items: left;
`;
const DurationContainer = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  font-size: 10px;
  color: ${DARK_GREY};
`;
const CreateByContainer = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  font-size: 10px;
  color: ${DARK_GREY};
`;

const Select = styled(StyledSelect)`
  margin-top: -2px;
  padding-bottom: 2px;
  width: 200px;
`;

const lookupToOption = (
  selectedValue: string,
  options: Array<any>,
): ILookupOption => {
  const result = options.find((option) => {
    if (option?.options?.length > 0) {
      const found = option.options.find((subOption: any) => subOption.value === selectedValue);
      return found !== undefined;
    }
    return option.value === selectedValue;
  });

  if (result && result?.options?.length) {
    return result.options.find((subOption: any) => subOption.value === selectedValue) as ILookupOption;
  }
  return result as ILookupOption;
};

interface IOwnProps {
  duration: string;
  clipIndex: number;
  clip: IVideoClip;
}

interface IStateProps {
  clipList: IVideoClip[];
  selectedClipIds: string[];
  isExchangeLiveEvent: boolean;
  exchangeQualifierOptions: SelectOptionType<string>[];
}

interface IDispatchProps {
  toggleClipSelection: (clipIndex: number) => void;
  updateClipFields: (clipIndex: number, updates: IClipFieldUpdates) => void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps { }

const ClipItemLeft: FC<IProps> = (props: IProps) => {
  const handleNameChange = (name: string) => {
    const {
      clipIndex,
      updateClipFields,
    } = props;
    updateClipFields(clipIndex, { name } as IClipFieldUpdates);
  };

  const {
    duration,
    clipIndex,
    clip,
    clipList,
    toggleClipSelection,
    selectedClipIds,
    isExchangeLiveEvent,
    exchangeQualifierOptions,
  } = props;
  const { name, markInTime, markOutTime } = clip;
  const selected = selectedClipIds.includes(clipList[clipIndex].id);
  const isSharedClip = !!clip.userId; // If userId exists then it is shared
  const ownerName = clip.owner;
  const disabled = !(markInTime && markOutTime);

  return (
    <Container>
      <Checkbox
        color="secondary"
        checked={selected}
        disabled={disabled}
        onChange={(e) => toggleClipSelection(clipIndex)}
      />
      <DragIndicatorIcon isDisabled={isSharedClip} />
      <NameAndDurationContainer>
        {isExchangeLiveEvent ? (
          <Select
            menuPlacement="auto"
            value={lookupToOption(name, exchangeQualifierOptions)}
            options={exchangeQualifierOptions}
            onChange={(option: unknown) => { handleNameChange((option as ILookupOption)?.value); }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
          />
        ) : (
          <NameEditor
            isDisabled={isSharedClip}
            name={name}
            onChange={handleNameChange}
          />
        )}
        <DurationContainer>{`Duration: ${duration}s`}</DurationContainer>
        {ownerName
          && <CreateByContainer>{`Owner: ${ownerName}`}</CreateByContainer>}
      </NameAndDurationContainer>
    </Container>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  clipList: selectIsInit(state) ? state.clipList.allClips : [],
  selectedClipIds: state.clipList.selectedClipIds,
  isExchangeLiveEvent: state.clipperMediaContent.isExchangeLiveEvent,
  exchangeQualifierOptions: optionsToReactSelect(selectExchangeQualifiersOptions(state)),
});

const mapDispatchToProps: IDispatchProps = {
  toggleClipSelection,
  updateClipFields,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClipItemLeft);
